import React, { useState, useEffect } from "react";
import { AsYouType, parsePhoneNumberFromString } from "libphonenumber-js";
import { Save } from "lucide-react";

export default function PartnerForm({
  onSubmit,
  isLoading,
  submitLabel = "Create Partner",
  defaultValues = null
}) {
  const [formData, setFormData] = useState({
    partnerName: "",
    pocName: "",
    pocEmail: "",
    pocPhone: "",
    address: "",
    industry: "",
    companySize: "",
    city: "",
    state: "",
    country: ""
  });

  const [errors, setErrors] = useState({});
  const sizeRegex = /^([1-9]\d*)-([1-9]\d*)$|^([1-9]\d*)\+$/;

  useEffect(() => {
    if (defaultValues) {
      const phoneObj = defaultValues?.poc?.number
        ? parsePhoneNumberFromString(defaultValues.poc.number)
        : null;

      setFormData({
        partnerName: defaultValues.partnerName || "",
        pocName: defaultValues.poc?.name || "",
        pocEmail: defaultValues.poc?.email || "",
        pocPhone: phoneObj ? phoneObj.formatInternational() : "",
        address: defaultValues.address || "",
        industry: defaultValues.companyDetails?.industry || "",
        companySize: defaultValues.companyDetails?.size || "",
        city: defaultValues.location?.city || "",
        state: defaultValues.location?.state || "",
        country: defaultValues.location?.country || ""
      });
    } else {
      resetForm();
    }
    
    setErrors({});
  }, [defaultValues]);

  const resetForm = () => {
    setFormData({
      partnerName: "",
      pocName: "",
      pocEmail: "",
      pocPhone: "",
      address: "",
      industry: "",
      companySize: "",
      city: "",
      state: "",
      country: ""
    });
  };

  const isValidEmail = (value) =>
    /^[^\s@]+@[^\s@]+\.[A-Za-z]{2,}$/.test(value);

  const validateForm = () => {
    const e = {};

    if (!formData.partnerName.trim() || formData.partnerName.length < 3)
      e.partnerName = "Partner name must be at least 3 characters";

    if (!formData.pocName.trim() || formData.pocName.length < 3)
      e.pocName = "POC name must be at least 3 characters";

    if (!isValidEmail(formData.pocEmail))
      e.pocEmail = "Enter a valid email address";

    if (!formData.pocPhone.trim()) {
      e.pocPhone = "Phone number is required";
    } else {
      const phoneObj = parsePhoneNumberFromString(formData.pocPhone);
      if (!phoneObj || !phoneObj.isValid())
        e.pocPhone = "Enter a valid phone number";
    }

    if (!formData.address.trim())
      e.address = "Address is required";

    if (!formData.industry.trim())
      e.industry = "Industry is required";

    if (!formData.companySize.trim()) {
      e.companySize = "Company size is required";
    } else if (!sizeRegex.test(formData.companySize.trim())) {
      e.companySize =
        "Company size must be like 1-10, 11-50, 51-200, or 500+";
    } else {
      // Extra safety for range values
      if (formData.companySize.includes("-")) {
        const [min, max] = formData.companySize.split("-").map(Number);
        if (!min || !max || min >= max) {
          e.companySize = "Company size range is invalid";
        }
      }
    }

    if (!formData.city.trim())
      e.city = "City is required";

    if (!formData.state.trim())
      e.state = "State is required";

    if (!formData.country.trim())
      e.country = "Country is required";

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const phoneObj = parsePhoneNumberFromString(formData.pocPhone);

    onSubmit({
      partnerName: formData.partnerName.trim(),
      poc: {
        name: formData.pocName.trim(),
        email: formData.pocEmail.trim(),
        number: phoneObj.number
      },
      address: formData.address.trim(),
      companyDetails: {
        industry: formData.industry.trim(),
        size: formData.companySize.trim()
      },
      location: {
        city: formData.city.trim(),
        state: formData.state.trim(),
        country: formData.country.trim()
      }
    });
  };

  const inputBase =
    "w-full h-10 px-3 rounded-lg border focus:ring-0 transition";

  const textareaBase =
    "w-full px-3 py-2 rounded-lg border focus:ring-0 transition resize-none";

  return (
    <form onSubmit={handleSubmit} className="space-y-6 pl-1">

      {/* PARTNER NAME */}
      <Field label="Partner Name" error={errors.partnerName}>
        <input
          className={inputBase}
          style={{
            background: 'var(--input-background)',
            color: 'var(--main-text)',
            borderColor: 'var(--card-border)'
          }}
          placeholder="BlueWave Technologies Inc"
          value={formData.partnerName}
          onChange={(e) =>
            setFormData({ ...formData, partnerName: e.target.value })
          }
        />
      </Field>

      {/* POC SECTION */}
      <div className="border rounded-lg p-4 space-y-4" style={{ borderColor: 'var(--card-border)', background: 'var(--card-bg)' }}>
        <h4 className="text-sm font-semibold" style={{ color: 'var(--main-text-muted)' }}>
          Point of Contact (POC)
        </h4>

        <Field label="Name" error={errors.pocName}>
          <input
            className={inputBase}
            style={{
              background: 'var(--input-background)',
              color: 'var(--main-text)',
              borderColor: 'var(--card-border)'
            }}
            placeholder="Michael Johnson"
            value={formData.pocName}
            onChange={(e) =>
              setFormData({ ...formData, pocName: e.target.value })
            }
          />
        </Field>

        <Field label="Email" error={errors.pocEmail}>
          <input
            className={inputBase}
            style={{
              background: 'var(--input-background)',
              color: 'var(--main-text)',
              borderColor: 'var(--card-border)'
            }}
            placeholder="michael.johnson@bluewave.com"
            value={formData.pocEmail}
            onChange={(e) =>
              setFormData({ ...formData, pocEmail: e.target.value })
            }
          />
        </Field>

        <Field label="Phone" error={errors.pocPhone}>
          <input
            className={inputBase}
            style={{
              background: 'var(--input-background)',
              color: 'var(--main-text)',
              borderColor: 'var(--card-border)'
            }}
            placeholder="+1 415 555 2671"
            value={formData.pocPhone}
            onChange={(e) =>
              setFormData({
                ...formData,
                pocPhone: new AsYouType().input(e.target.value)
              })
            }
          />
        </Field>
      </div>

      {/* ADDRESS */}
      <Field label="Address" error={errors.address}>
        <textarea
          rows={3}
          className={textareaBase}
          style={{
            background: 'var(--input-background)',
            color: 'var(--main-text)',
            borderColor: 'var(--card-border)'
          }}
          placeholder="450 Market Street, Suite 1200"
          value={formData.address}
          onChange={(e) =>
            setFormData({ ...formData, address: e.target.value })
          }
        />
      </Field>

      {/* COMPANY DETAILS */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Industry" error={errors.industry}>
          <input
            className={inputBase}
            style={{
              background: 'var(--input-background)',
              color: 'var(--main-text)',
              borderColor: 'var(--card-border)'
            }}
            placeholder="Software Services"
            value={formData.industry}
            onChange={(e) =>
              setFormData({ ...formData, industry: e.target.value })
            }
          />
        </Field>

        <Field label="Company Size" error={errors.companySize}>
          <input
            className={inputBase}
            style={{
              background: 'var(--input-background)',
              color: 'var(--main-text)',
              borderColor: 'var(--card-border)'
            }}
            placeholder="201-500 or 1000+"
            value={formData.companySize}
            onChange={(e) =>
              setFormData({
                ...formData,
                companySize: e.target.value.replace(/\s/g, "")
              })
            }
          />
        </Field>
      </div>

      {/* LOCATION */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Field label="Country" error={errors.country}>
          <input
            className={inputBase}
            style={{
              background: 'var(--input-background)',
              color: 'var(--main-text)',
              borderColor: 'var(--card-border)'
            }}
            placeholder="United States"
            value={formData.country}
            onChange={(e) =>
              setFormData({ ...formData, country: e.target.value })
            }
          />
        </Field>

        <Field label="State" error={errors.state}>
          <input
            className={inputBase}
            style={{
              background: 'var(--input-background)',
              color: 'var(--main-text)',
              borderColor: 'var(--card-border)'
            }}
            placeholder="California"
            value={formData.state}
            onChange={(e) =>
              setFormData({ ...formData, state: e.target.value })
            }
          />
        </Field>

        <Field label="City" error={errors.city}>
          <input
            className={inputBase}
            style={{
              background: 'var(--input-background)',
              color: 'var(--main-text)',
              borderColor: 'var(--card-border)'
            }}
            placeholder="San Francisco"
            value={formData.city}
            onChange={(e) =>
              setFormData({ ...formData, city: e.target.value })
            }
          />
        </Field>
      </div>

      {/* SUBMIT */}
      <button
        type="submit"
        disabled={isLoading}
        className={`w-full py-3 rounded-lg h-9 font-medium flex items-center justify-center gap-2 ${isLoading ? 'cursor-not-allowed' : ''}`}
        style={{
          background: isLoading ? 'var(--card-border)' : 'var(--brand)',
          color: '#fff'
        }}
      >
        <Save size={16} />
        {isLoading
          ? submitLabel === "Update Partner"
            ? "Updating Partner..."
            : "Creating Partner..."
          : submitLabel}
      </button>

    </form>
  );
}

const Field = ({ label, error, children }) => (
  <div>
    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--main-text-muted)' }}>
      {label} <span style={{ color: 'var(--danger)' }}>*</span>
    </label>
    {children}
    {error && <p className="mt-1 text-xs" style={{ color: 'var(--danger)' }}>{error}</p>}
  </div>
);

